// components/common/link/link-component.tsx
import * as Scrivito from 'scrivito';
import { getLinkTitle } from '../../../utils/scrivito';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

export interface LinkComponentProps extends React.ComponentProps<typeof Scrivito.LinkTag> {
  accessibilityText?: string;
  title?: string | ReactNode | ReactNode[];
}

export const LinkComponent: React.FC<LinkComponentProps> = Scrivito.connect(
  ({ to, accessibilityText, title, children, ...rest }) => {
    const { t } = useTranslation();

    const constructAriaLabel = (): string | undefined => {
      let text = accessibilityText || (typeof linkTitle === 'string' ? linkTitle : '');

      if (to instanceof Scrivito.Link) {
        const isExternal = to.isExternal();
        const opensNewTab = to.target() === '_blank';

        if (isExternal && opensNewTab) {
          text += `, ${t('component.accessibility.opensExternalLinkInNewTab')}`;
        } else if (isExternal) {
          text += `, ${t('component.accessibility.opensExternalLink')}`;
        } else if (opensNewTab) {
          text += `, ${t('component.accessibility.opensNewTab')}`;
        }
      }

      return text || undefined;
    };

    const constructLinkTitle = (): string | ReactNode | ReactNode[] | null => {
      if (title !== undefined && title !== null) {
        return title;
      }

      if (to instanceof Scrivito.Link) {
        return getLinkTitle(to);
      }

      return null;
    };

    const linkTitle = constructLinkTitle();
    const ariaLabel = constructAriaLabel();

    return (
      <Scrivito.LinkTag
        to={to}
        title={typeof linkTitle === 'string' ? linkTitle : undefined}
        aria-label={ariaLabel}
        {...rest}
      >
        {children || linkTitle}
      </Scrivito.LinkTag>
    );
  }
);
